import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MaterialModal from "../../app/material-modal";
import { inputCommonStyle } from "../../../util/admin/cssCommonStyle";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useKeycloak } from "@react-keycloak/web";
import { useAtom } from "jotai";
import { useFormik } from "formik";
import { useCreateNewGroup } from "../../../tanstackQuery/queries/superAdmin/group";
import { useQueryClient } from "@tanstack/react-query";
import { CreateGroupData } from "../../../tanstackQuery/queries/superAdmin/group/type";
import { toast } from "sonner";
import { WeekEndDay } from "../../../constants";
import { SAdminBaseAtom } from "../../../stores/jotai/super-admin/superAdminBase";

interface FormValues {
  name: string;
  canvasType: string;
  weekEndDay: string;
  timeZone: string;
  time: Dayjs;
  startDate: Dayjs;
  endDate: Dayjs;
}
const validate = (values: FormValues) => {
  const errors: Partial<FormValues> = {};
  if (!values.name) {
    errors.name = "Required";
  }
  return errors;
};
const AddCohortModal = ({ open, handleClose, parentGroupID }) => {
  const { keycloak } = useKeycloak();
  const [sAdminBase,]=useAtom(SAdminBaseAtom); 
  const client = useQueryClient();
  const {
    mutateAsync: createCohort,
    isPending,
    isSuccess,
  } = useCreateNewGroup(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["orgtree"] });
        handleClose();
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      canvasType: "BusinessModelCanvas",
      name: "",
      weekEndDay: "Saturday",
      timeZone: "PST",
      startDate: dayjs(),
      endDate: dayjs(),
      time: dayjs(),
    },
    validate,
    onSubmit: (values) => {
      handleSumitNewTeam(values);
    },
  });
  const handleSumitNewTeam = async (values) => {
    const newCohortData: CreateGroupData = {
      name: values.name,
      orgID: sAdminBase?.selectedOrg?.orgID,
      groupType: "Cohort",
      canvasType: "BusinessModelCanvas",
      parentGroupID: parentGroupID === "root" ? "root" : parentGroupID,
      weekEndDay: values.weekEndDay,
      startDate: values.startDate.format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
      endDate: values.endDate.format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
      timeZone: values.timeZone,
      time: values.time.format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
    };
    try {
      await createCohort(newCohortData);
      if (isSuccess) {
        toast.success("New Cohort created successfully!");
        formik.resetForm();
        handleClose();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create Cohort.");
    }
  };
  return (
    <MaterialModal
      open={open}
      handleDialogClose={handleClose}
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            minHeight: "300px",
            minWidth: "900px",
            ".MuiDialogContent-root": {
              overflow: "auto",
            },
          },
        },
      }}
      title="Create Cohort"
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack
          direction="row"
          justifyContent="space-around"
          sx={{
            backgroundColor: "white",
            display: "flex",
            borderRadius: "4px",
            p: "16px 12px",
            my: 2,
          }}
        >
          <Stack direction="column" justifyContent="center" gap={2}>
            <Stack direction="row" gap={5} alignItems="center">
              <Box sx={{ flexBasis: "40%" }}>
                <Typography sx={{ fontWeight: "bold" }}>
                  {" "}
                  Cohort Name
                </Typography>
              </Box>
              <Box sx={{ flexBasis: "60%" }}>
                <TextField
                  name="name"
                  id="outlined-basic"
                  variant="outlined"
                  size="small"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  fullWidth
                  sx={inputCommonStyle}
                  error={formik.submitCount > 0 && Boolean(formik.errors.name)}
                  helperText={formik.submitCount > 0 && formik.errors.name}
                />
              </Box>
            </Stack>
            <Stack direction="row" gap={5} alignItems="center">
              <Box sx={{ flexBasis: "40%" }}>
                <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>
                  Start Date
                </Typography>
              </Box>
              <Box sx={{ flexBasis: "60%" }}>
                <DatePicker
                  value={formik.values.startDate}
                  onChange={(newValue) =>
                    formik.setFieldValue("startDate", newValue)
                  }
                  sx={{
                    ".MuiInputBase-input": {
                      padding: "8px 8px",
                      fontSize: "14px",
                    },
                  }}
                />
              </Box>
            </Stack>
            <Stack direction="row" gap={5} alignItems="center">
              <Box sx={{ flexBasis: "40%" }}>
                <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>
                  End Date
                </Typography>
              </Box>
              <Box sx={{ flexBasis: "60%" }}>
                <DatePicker
                  value={formik.values.endDate}
                  onChange={(newValue) =>
                    formik.setFieldValue("endDate", newValue)
                  }
                  sx={{
                    ".MuiInputBase-input": {
                      padding: "8px 8px",
                      fontSize: "14px",
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>

          <Stack direction="column" justifyContent="center" gap={2}>
            <Stack direction="row" gap={5} alignItems="center">
              <Box sx={{ flexBasis: "40%" }}>
                <Typography sx={{ fontWeight: "bold" }}> Reset Day</Typography>
              </Box>
              <Box sx={{ flexBasis: "60%" }}>
                <FormControl sx={inputCommonStyle} fullWidth>
                  <Select
                    name="weekEndDay"
                    size="small"
                    value={formik.values.weekEndDay}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                     {
                      Object?.keys(WeekEndDay)?.map((value,index)=>(
                          <MenuItem key={index+value} value={value}>{value}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Box>
            </Stack>
            <Stack direction="row" gap={5} alignItems="center">
              <Box sx={{ flexBasis: "40%" }}>
                <Typography sx={{ fontWeight: "bold" }}> Time Zone</Typography>
              </Box>
              <Box sx={{ flexBasis: "60%" }}>
                <FormControl sx={inputCommonStyle} fullWidth>
                  <Select
                    name="timeZone"
                    size="small"
                    value={formik.values.timeZone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <MenuItem value="PST">PST</MenuItem>
                    <MenuItem value="CST">CST</MenuItem>
                    <MenuItem value="EST">EST</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
            <Stack direction="row" gap={5} alignItems="center">
              <Box sx={{ flexBasis: "40%" }}>
                <Typography sx={{ fontWeight: "bold" }}>Time</Typography>
              </Box>
              <Box sx={{ flexBasis: "60%" }}>
                <TimePicker
                  value={formik.values.time}
                  onChange={(newValue) =>
                    formik.setFieldValue("time", newValue)
                  }
                  sx={{
                    ".MuiInputBase-input": {
                      padding: "8px 8px",
                      fontSize: "14px",
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mr: 5 }}
        >
          <Button variant="outlined" onClick={() => handleClose()}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isPending}
            endIcon={isPending ? <CircularProgress size={16} /> : null}
          >
            create
          </Button>
        </Box>
      </form>
    </MaterialModal>
  );
};

export default AddCohortModal;
